import {
  CheckType,
  Comparison,
  NativeResources,
  Option,
} from '@ariksa/inventory-core/api';
import { Check } from '@ariksa/inventory-core/dist/api';
import { isEmpty, some } from 'lodash';

// Validity helpers for the criteria step. Next-disabled is derived from the
// saved criteria of every row instead of a single flag each row overwrote.

const hasCondition = (selectedConditions, value: string) =>
  some(selectedConditions, { value });

// True when the form data already holds values for the given condition.
export const hasConditionData = (formData, condition: string) => {
  if (condition === 'region') return !isEmpty(formData?.region);
  if (condition === 'tag_keys_condition')
    return !isEmpty(formData?.criteria?.tag_keys_condition?.key);
  return !isEmpty(formData?.criteria?.[condition]);
};

export const isTagKeyValueCriterionIncomplete = criterion =>
  isEmpty(criterion?.value);

// An EC2 row needs an OS/technology and a version or severity.
export const isResourceTypeCriterionIncomplete = criterion =>
  criterion?.resource_type === NativeResources.Ec2Instance &&
  (isEmpty(criterion?.value_1) || isEmpty(criterion?.value_2));

export const hasEC2ResourceCriteria = (
  selectedConditions,
  resourceTypeCriteria,
) =>
  hasCondition(selectedConditions, 'resource_type_criteria') &&
  some(resourceTypeCriteria, {
    resource_type: NativeResources.Ec2Instance,
  });

export const isCriteriaStepIncomplete = (
  selectedConditions,
  { formData, tagKeyValueCriteria, resourceTypeCriteria },
) => {
  if (isEmpty(selectedConditions)) return true;
  const incomplete = (condition: string, criteria, isIncomplete) =>
    hasCondition(selectedConditions, condition) &&
    (isEmpty(criteria) || some(criteria, isIncomplete));
  return (
    (hasCondition(selectedConditions, 'region') &&
      !hasConditionData(formData, 'region')) ||
    (hasCondition(selectedConditions, 'tag_keys_condition') &&
      !hasConditionData(formData, 'tag_keys_condition')) ||
    incomplete(
      'tag_key_values_condition',
      tagKeyValueCriteria,
      isTagKeyValueCriterionIncomplete,
    ) ||
    incomplete(
      'resource_type_criteria',
      resourceTypeCriteria,
      isResourceTypeCriterionIncomplete,
    )
  );
};

// Default EC2 criterion (OS + version), matching the dropdowns' defaults.
export const getEC2CriterionDefaults = osOptions => ({
  check_type: CheckType.Contains,
  key: Option.Os,
  value_1: osOptions?.[0]?.value,
  check: Check.Version,
  comparison: Comparison.EqualTo,
  value_2: osOptions?.[0]?.data?.[0]?.version,
});

export const emptyEC2Criterion = {
  check_type: undefined,
  key: undefined,
  value_1: undefined,
  check: undefined,
  comparison: undefined,
  value_2: undefined,
};
